import React, { useEffect } from "react";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../helper/formatCurrency";
import { REPAIR_BLOCKED_STATUSES } from "./utils/constants";
import {
  OrderDialogShell,
  OrderSummaryDetails,
  HeaderBadge,
  InfoTile,
  DialogSection,
  computeOrderTotals,
  dialogBtn,
  dialogSelect,
} from "./OrderDialog";

const STATUS_LABELS = {
  pending: "Pending",
  accept: "Accepted",
  "delivery pickup": "Delivery pickup",
  "out for delivery": "Out for delivery",
  completed: "Completed",
  reject: "Rejected",
};

const OrderPopup = ({
  popupOrder,
  rows,
  driverList,
  driverId,
  driverName,
  driverMobile,
  useNewDriver,
  isFinalStagePopup,
  reasons,
  selectedRejectReason,
  isRejecting,
  setPopupOrder,
  setDriverId,
  setDriverName,
  setDriverMobile,
  setUseNewDriver,
  setIsRejecting,
  setSelectedRejectReason,
  updatePopupOrderStatus,
  updatePopupOrderStatusReject,
  getNextStatuses,
  toast,
  setSelectedOrder,
}) => {
  const { locale, gstFraction, taxLabel } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  // Reset rejecting state when order changes
  useEffect(() => {
    setIsRejecting(false);
    setSelectedRejectReason("");
  }, [popupOrder, setIsRejecting, setSelectedRejectReason]);

  if (!popupOrder) return null;

  const products = rows || [];

  // Check if products were modified — then totals are recalculated from rows
  const isEditing =
    products.length !== popupOrder?.products?.length ||
    products.some((r, i) => {
      const original = popupOrder?.products?.[i];
      return (
        !original ||
        r.quantity !== original.quantity ||
        r.price !== original.price ||
        r.offerPrice !== original.offerPrice
      );
    });
  const totals = computeOrderTotals(popupOrder, products, gstFraction, !isEditing);

  const showDriverSelection = popupOrder.status === "accept" && !isFinalStagePopup;
  const canModify = !REPAIR_BLOCKED_STATUSES.includes(popupOrder.status);
  const nextStatuses = getNextStatuses(popupOrder.status);

  const handleStatusClick = (status) => {
    if (popupOrder.status === "accept" && useNewDriver && driverMobile.length < 10) {
      toast.error("Please enter a valid 10-digit driver mobile number.");
      return;
    }
    // Additional validation when moving to delivery pickup
    if (status === "delivery pickup" && !driverId && !useNewDriver) {
      toast.error("Please select a driver or add a new driver before proceeding.");
      return;
    }
    updatePopupOrderStatus(status);
  };

  const statusButtonLabel = (status) => {
    if (popupOrder.status === "accept" && status === "delivery pickup") {
      return "Assign driver & start pickup";
    }
    if (status === "accept") return "Accept";
    if (status === "completed") return "Mark completed";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const extraTiles = (
    <>
      {popupOrder.assignedDriver && (
        <InfoTile
          label="Driver"
          className="col-span-2"
          value={`${popupOrder.assignedDriver?.name || ""} - ${popupOrder.assignedDriver?.mobile || ""}`}
        />
      )}
      {popupOrder.status === "reject" && popupOrder.rejectReason && (
        <InfoTile label="Reject reason" className="col-span-2" value={popupOrder.rejectReason} warn />
      )}
    </>
  );

  const footer = isRejecting ? (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <select
        className={`${dialogSelect} sm:flex-1 min-w-0`}
        value={selectedRejectReason}
        onChange={(e) => setSelectedRejectReason(e.target.value)}
        autoFocus
      >
        <option value="">Reason for rejection</option>
        {reasons.map((reason, index) => (
          <option key={index} value={reason}>
            {reason}
          </option>
        ))}
      </select>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          className={`${dialogBtn} border border-gray-200 text-gray-700 hover:bg-gray-50`}
          onClick={() => {
            setIsRejecting(false);
            setSelectedRejectReason("");
          }}
        >
          Back
        </button>
        <button
          type="button"
          className={`${dialogBtn} bg-red-600 hover:bg-red-700 text-white`}
          onClick={() => {
            if (!selectedRejectReason) {
              toast.error("Please select a rejection reason.");
              return;
            }
            updatePopupOrderStatusReject("reject", selectedRejectReason);
          }}
        >
          Confirm reject
        </button>
      </div>
    </div>
  ) : (
    <div className="flex flex-wrap items-center justify-between gap-3">
      {canModify ? (
        <button
          type="button"
          className="text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors"
          onClick={() => {
            setSelectedOrder(popupOrder);
            setPopupOrder(null);
          }}
        >
          🔧 Modify order
        </button>
      ) : (
        <span />
      )}
      <div className="flex flex-wrap justify-end gap-2">
        {nextStatuses.map((status) =>
          status === "reject" ? (
            <button
              key="reject-btn"
              type="button"
              className={`${dialogBtn} border border-gray-200 text-red-600 hover:bg-red-50 hover:border-red-200`}
              onClick={() => setIsRejecting(true)}
            >
              Reject
            </button>
          ) : (
            <button
              key={status}
              type="button"
              className={`${dialogBtn} text-white ${
                status === "accept" || status === "completed"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              onClick={() => handleStatusClick(status)}
            >
              {statusButtonLabel(status)}
            </button>
          ),
        )}
      </div>
    </div>
  );

  return (
    <OrderDialogShell
      labelId="manage-order-title"
      status={popupOrder.status}
      title="Manage order"
      subtitle={`Order #${popupOrder.orderId}`}
      icon="🧾"
      badge={<HeaderBadge>{STATUS_LABELS[popupOrder.status] || popupOrder.status}</HeaderBadge>}
      onClose={() => setPopupOrder(null)}
      footer={nextStatuses.length > 0 || canModify || isRejecting ? footer : null}
    >
      <OrderSummaryDetails
        order={popupOrder}
        rows={products}
        totals={totals}
        currencySymbol={currencySymbol}
        taxLabel={taxLabel}
        extraTiles={extraTiles}
      />

      {/* Driver Selection (ONLY for accept) */}
      {showDriverSelection && (
        <DialogSection
          tone="blue"
          title="📦 Assign driver for delivery"
          hint="Select an existing driver or add a new one to proceed with delivery pickup"
        >
          <select
            className={dialogSelect}
            value={driverId}
            onChange={(e) => setDriverId(e.target.value)}
          >
            <option value="">Select driver</option>
            {driverList.map((d) => (
              <option key={d._id} value={d._id}>
                {d.name} - {d.mobile}
              </option>
            ))}
          </select>

          {!useNewDriver ? (
            <button
              type="button"
              className="text-sm font-medium text-blue-600 hover:text-blue-800"
              onClick={() => setUseNewDriver(true)}
            >
              + Add new driver
            </button>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                className={dialogSelect}
                placeholder="Driver name"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
              />
              <input
                className={dialogSelect}
                placeholder="Driver mobile (10 digits)"
                inputMode="numeric"
                value={driverMobile}
                maxLength={10}
                onChange={(e) => setDriverMobile(e.target.value.replace(/\D/g, ""))}
              />
            </div>
          )}
        </DialogSection>
      )}
    </OrderDialogShell>
  );
};

export default OrderPopup;
